import { storeConfInCart } from '@eliadmin/core/src/actions/proform-actions';
import { useCartSnapshot } from '@eliadmin/core/src/hooks/cart/use-cart-snapshot';
import { addToCart, updateCart } from '@eliadmin/core/src/api/cart/cart-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useCartPresence } from '@eliadmin/core/src/hooks/use-cart-presence';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import type { EnhancedProductConf } from '@eliadmin/core/src/stores/configurator-store';
import {
  useConfiguratorIdRotate,
  useConfiguratorStore,
  useProformQuery,
} from '@eliadmin/core/src/stores/configurator-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS,
  buildProductCode,
  getAttributesFromConf,
} from '@eliadmin/core/src/utils/proform-utils';
import { buildConfiguratorHref } from '@eliadmin/core/src/utils/configurator-route-utils';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback } from 'react';

function getKaratErrorMessage(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;

  const data = payload as Record<string, unknown>;
  const fields =
    data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
      ? (data.errors as Record<string, unknown>)
      : data;
  const value = fields.karat ?? fields.KARAT;
  const message =
    typeof value === 'string'
      ? value
      : Array.isArray(value) && value.every(item => typeof item === 'string')
        ? value.join('')
        : undefined;

  return message || (typeof data.message === 'string' ? data.message : undefined);
}

export function useProformAddToCart() {
  const { fullResponseLifecycle, client, cartOptions } = useCartSnapshot();
  const { toast } = useToast();
  const authToken = useCustomerStore(s => s.me?.authToken);
  const configurator = useConfiguratorStore();
  const { data: productConfId } = useProformQuery('GET_PRODUCT_CONF_ID', {
    user_konf_id: configurator.user_konf_id,
  });
  const { t } = useTranslations();

  const locale = useLocaleStore(s => s.currentLocale);

  const router = useRouter();
  const pathname = usePathname();
  const cartPresence = useCartPresence();
  const setUserKonfId = useConfiguratorStore(s => s.setUserKonfId);
  const showSuccessToast = useCallback(() => {
    toast({
      title: t('text1208'),
      variant: 'success',
      // description:''
      action: (
        <Button className="ea-toast-action flex w-fit items-center justify-center rounded-full bg-[#333333] px-8 py-3 text-center text-secondary">
          <Link href={ROUTES.CART()}>{t('text456')}</Link>
        </Button>
      ),
    });
  }, [t, toast]);

  const priceWithoutVat = Number(productConfId.root.smako_conf.column.cena.replace(',', '.')) ?? 0;
  const qty = Number(productConfId.root.smako_conf.column.mnozstvi.replace(',', '.')) ?? 0;
  const rotateConfIdMutation = useConfiguratorIdRotate({
    onSuccess: newConfId => {
      router.replace(
        buildConfiguratorHref(pathname, {
          confId: newConfId,
          cartHash: configurator.cart_hash,
          returnPath: configurator.returnPath,
        }),
      );
      showSuccessToast();
    },
  });

  const handleCartMutationError = (error: unknown) => {
    if (error instanceof ValidationError) {
      const errorMessage =
        getKaratErrorMessage(error.fields) ||
        getKaratErrorMessage('payload' in error ? error.payload : undefined) ||
        (error.message !== ValidationError.statusText ? error.message : undefined);

      if (errorMessage) {
        toast({
          // TODO: translate
          title: t('text1471'),
          variant: 'destructive',
          description: errorMessage.replace('#ZSHOP_ERROR_TITLE=', ''),
        });
        return;
      }
    }

    if (error) {
      toast({
        // TODO: translate
        title: t('text1472'),
        variant: 'destructive',
      });
    }
  };

  const storeMutation = useMutation({
    mutationFn: () => {
      return storeConfInCart({
        locale,
        authToken,
        data: {
          code: buildProductCode(
            productConfId.root.smako_conf.column.cislo_nomenklatury,
            configurator.user_konf_id,
          ),
          conf_id: configurator.user_konf_id,
          height:
            Number(productConfId.root.smako_conf.column.user_vyska) ??
            Number(configurator.productDetail.root.smako_nom_default.default.user_vyska) ??
            1,
          width:
            Number(productConfId.root.smako_conf.column.user_sirka) ??
            Number(configurator.productDetail.root.smako_nom_default.default.user_sirka) ??
            1,
          length: 10,
          price: Number(priceWithoutVat.toFixed(2)),
          price_with_vat: Number((priceWithoutVat * 1.21).toFixed(2)),
          qty,
          // TODO: weight
          weight: 1,
          image: `${env.NEXT_PUBLIC_API_URL}/files/fallback.jpg`,
        },
      });
    },
  });
  const addToCartMutation = useMutation({
    meta: { affectsCart: true },
    mutationFn: addToCart,
    scope: fullResponseLifecycle.scope,
    onMutate: fullResponseLifecycle.onMutate,
    onSuccess: async (resp, _variables, context) => {
      if (!context?.current()) return;
      setUserKonfId(resp.data.new_conf_id);
      router.replace(
        buildConfiguratorHref(pathname, {
          confId: resp.data.new_conf_id,
          cartHash: configurator.cart_hash,
          returnPath: configurator.returnPath,
        }),
      );
      showSuccessToast();
      // Consume the POST identifier before the next GET can clear it.
      await client.invalidateQueries({ queryKey: cartOptions.queryKey, exact: true });
    },
    onError: handleCartMutationError,
  });

  const updateCartMutation = useMutation({
    meta: { affectsCart: true },
    mutationFn: updateCart,
    ...fullResponseLifecycle,
    onSuccess: async (response, variables, context) => {
      if (!context?.current()) return;
      // Consume the one-shot KARAT identifier before any following cart GET.
      if (response.data.new_conf_id) {
        setUserKonfId(response.data.new_conf_id);
        router.replace(buildConfiguratorHref(pathname, {
          confId: response.data.new_conf_id,
          cartHash: configurator.cart_hash,
          returnPath: configurator.returnPath,
        }));
        showSuccessToast();
      } else {
        rotateConfIdMutation.mutate();
      }
      await fullResponseLifecycle.onSuccess(response, variables, context);
    },
    onError: (error, variables, context) => {
      fullResponseLifecycle.onError(error, variables, context);
      handleCartMutationError(error);
    },
  });

  async function compoundMutation(confEnhanced: EnhancedProductConf) {
    if (qty < 1) {
      toast({
        title: t('text1473'),
        variant: 'destructive',
        description: t('text1474'),
      });
      return;
    }

    const [error, res] = await storeMutation.mutateAsync();

    if (error) {
      toast({
        title: t('text1472'),
        variant: 'destructive',
        description: error,
      });
      return;
    }
    if (!res) return;

    const product = res.data as ProductDetailData;

    if (cartPresence) {
      updateCartMutation.mutate({
        authToken,
        body: {
          hash: cartPresence.hash,
          qty,
          custom_attributes: getAttributesFromConf(
            productConfId,
            configurator.productCols,
            true,
            GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS,
            t,
            confEnhanced,
          ),
          item: {
            product: {
              id: product.id,
            },
            sub_items: {},
          },
        },
        locale,
      });
      return;
    }

    addToCartMutation.mutate({
      authToken,
      body: {
        item: {
          product: {
            id: product.id,
          },
          sub_items: {},
        },
        qty,
        custom_attributes: getAttributesFromConf(
          productConfId,
          configurator.productCols,
          true,
          GET_ATTRIBUTES_FROM_CONF_IGNORE_FIELDS,
          t,
          confEnhanced,
        ),
      },
      locale,
    });
  }

  return {
    mutate: compoundMutation,
    isPending:
      storeMutation.isPending || addToCartMutation.isPending || updateCartMutation.isPending || rotateConfIdMutation.isPending,
  };
}
